import type { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { FluentResultResponse, getFluentResultError } from '@/types/fluentResult';
import { AddAssignmentQueueAccountSubmitParams } from '@/features/serviceModal/components/AddAssignmentQueueAccountModal/types';
import { EditAssignmentQueueAccountSubmitParams } from '@/features/serviceModal/components/EditAssignmentQueueAccountModal/types';
import type { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { SnackType } from '@/features/snacks/enums/SnackType';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import {
  addAssignmentQueueAccount,
  deleteAccountFromAssignmentQueue,
  getAssignmentQueues,
  getAssignmentQueuesSuccess,
  incrementAssignmentQueuesPage,
  setLoadingAssignmentQueuesData,
  updateAssignmentQueueAccount,
} from '../store/assignmentQueueSlice';

function* getAssignmentQueuesSaga({ type }) {
  try {
    yield put(setLoadingAssignmentQueuesData(true));

    const ASSIGNMENT_QUEUE_INCLUDES = [
      `${EdsEntityCodes.AssignmentQueueMemberships}`,
      `${EdsEntityCodes.AssignmentQueueMemberships}.${EdsEntityCodes.Person}.${EdsEntityCodes.Accounts}`,
    ].join(',');

    const pagination = yield select((state: RootState) => state.assignmentQueuesSlice.data.pagination);
    const requestUrl = `/filter/${EdsEntityCodes.AssignmentQueue}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      includes: [ASSIGNMENT_QUEUE_INCLUDES],
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      section: EdsDefinitionSections.SystemPage,
    });

    EdsDebugService.logResponse(EdsEntityCodes.AssignmentQueue, {
      rawResponse: data,
      normalizedResponse,
    });

    yield put(
      getAssignmentQueuesSuccess({
        shouldRewrite: type !== incrementAssignmentQueuesPage.type,

        data: {
          listings: normalizedResponse,
          pagination: {
            ...data.pagination,
            isLastPage: pagination.page * pagination.perPage >= pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setLoadingAssignmentQueuesData(false));
  }
}

function* deleteAccountFromAssignmentQueueSaga({ payload: { id } }: PayloadAction<{ id: number | string }>) {
  try {
    const { data } = yield call(HttpService.delete<FluentResultResponse>, `/examiner/queue/membership/${id}`, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    const errorMessage = getFluentResultError(data);

    if (errorMessage) {
      SnacksService.add({
        type: SnackType.Error,
        title: errorMessage,
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
      return;
    }

    yield* getAssignmentQueuesSaga({ type: getAssignmentQueues.type });

    SnacksService.add({
      type: SnackType.Success,
      title: 'User was deleted successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to delete User, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  }
}

function* addAssignmentQueueAccountSaga({ payload }: PayloadAction<AddAssignmentQueueAccountSubmitParams>) {
  try {
    const { data } = yield call(HttpService.post<FluentResultResponse>, `/examiner/queue/membership`, payload, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    const errorMessage = getFluentResultError(data);

    if (errorMessage) {
      SnacksService.add({
        type: SnackType.Error,
        title: errorMessage,
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
      return;
    }

    yield* getAssignmentQueuesSaga({ type: getAssignmentQueues.type });

    SnacksService.add({
      type: SnackType.Success,
      title: 'User was added successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to add User, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  }
}

function* updateAssignmentQueueAccountSaga({ payload }: PayloadAction<EditAssignmentQueueAccountSubmitParams>) {
  try {
    const { data } = yield call(HttpService.put<FluentResultResponse>, '/examiner/queue/membership', payload, {
      baseURL: import.meta.env.VITE_API_URL_V2,
    });

    const errorMessage = getFluentResultError(data);

    if (errorMessage) {
      SnacksService.add({
        type: SnackType.Error,
        title: errorMessage,
        anchorOrigin: {
          horizontal: 'right',
          vertical: 'bottom',
        },
      });
      return;
    }

    yield* getAssignmentQueuesSaga({ type: getAssignmentQueues.type });

    SnacksService.add({
      type: SnackType.Success,
      title: 'User was updated successfully',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to update User, please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  }
}

export function* assignmentQueuesWatcher() {
  yield takeLatest([getAssignmentQueues.type, incrementAssignmentQueuesPage.type], getAssignmentQueuesSaga);
  yield takeLatest(deleteAccountFromAssignmentQueue.type, deleteAccountFromAssignmentQueueSaga);
  yield takeLatest(addAssignmentQueueAccount.type, addAssignmentQueueAccountSaga);
  yield takeLatest(updateAssignmentQueueAccount.type, updateAssignmentQueueAccountSaga);
}
